import React, { useEffect, useState } from "react";
import styled from "styled-components";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { FaFileExcel } from "react-icons/fa";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

const Container = styled.div`
  background-color: #f9f9f9;
  padding: 20px;
  position: relative;
`;

const Title = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  text-align: center;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;

  &:hover {
    color: #000;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  justify-content: center;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #1d6f42;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #155e36;
  }

  svg {
    margin-right: 6px;
  }
`;
const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #ccc;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 12px;
  background-color: #fff;
  font-size: 0.7rem;

  th, td {
    border: 1px solid #ddd;
    text-align: center;
    padding: 14px 8px;
    line-height: 1.4;
  }

  th {
    background: #dcb6f2;
  }

  tr:nth-child(even) {
    background-color: #f9f9f9;
  }

  tr:hover {
    background-color: #eef6ff;
  }
`;

const TopPurchaseTransactions = ({ data }) => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);

  const exportToExcel = () => {
    const rows = filteredTransactions.map((row) => ({
      Dostawca: row.contractor,
      "Wartość Zakupu (PLN)": row.value.toFixed(2),
      "Data Zakupu": row.date,
      "Dokument": row.document,
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "TopZakupy");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `top_zakupy_${selectedYear}_${selectedMonth}.xlsx`);
  };

  useEffect(() => {
    if (data && data.length > 0) {
      const mapped = data.map((row) => ({
        contractor: row.NazwaDostawcy || "Nieznany dostawca",
        value: parseFloat(row.K_42 || 0),
        date: row.DataZakupu || "Nieznana data",
        document: row.DowodZakupu || "Brak numeru",
        year: parseInt(row.Rok, 10),
        month: parseInt(row.Miesiac, 10),
      }));
      setTransactions(mapped);

      const years = [...new Set(mapped.map((t) => t.year))].sort((a, b) => b - a);
      setAvailableYears(years);
      if (years.length > 0) setSelectedYear(years[0]);
    }
  }, [data]);

  useEffect(() => {
    if (selectedYear) {
      const months = [...new Set(transactions.filter((t) => t.year === selectedYear).map((t) => t.month))].sort((a, b) => a - b);
      setAvailableMonths(months);
      if (months.length > 0) setSelectedMonth(months[months.length - 1]);
    }
  }, [selectedYear, transactions]);

  useEffect(() => {
    if (selectedYear && selectedMonth) {
      const filtered = transactions
        .filter((t) => t.year === selectedYear && t.month === selectedMonth)
        .sort((a, b) => b.value - a.value)
        .slice(0, 10);
      setFilteredTransactions(filtered);
    }
  }, [selectedYear, selectedMonth, transactions]);

  if (transactions.length === 0) return <p>Ładowanie danych...</p>;

  return (
    <Container>
      <Title>
        Top 10 Największych Zakupów
        <InfoIconWrapper data-tooltip-id="top-purchases-info">
          <Info />
        </InfoIconWrapper>
      </Title>

      <InfoTooltip
        id="top-purchases-info"
        place="right"
        content={
          <>
            <strong>Największe zakupy</strong>
            <br />
            Tabela pokazuje 10 największych zakupów w wybranym okresie.
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label>Rok:</label>
          <Select value={selectedYear || ""} onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}>
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </Select>
        </div>

        <div>
          <label>Miesiąc:</label>
          <Select value={selectedMonth || ""} onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}>
            {availableMonths.map((month) => (
              <option key={month} value={month}>
                {new Date(0, month - 1).toLocaleString("pl-PL", { month: "long" })}
              </option>
            ))}
          </Select>
        </div>

        <ExportButton onClick={exportToExcel}>
          <FaFileExcel />
          Eksportuj do Excel
        </ExportButton>
      </FiltersWrapper>

      <TableWrapper>
        <StyledTable>
          <thead>
            <tr>
              <th>Dostawca</th>
              <th>Wartość Zakupu</th>
              <th>Data Zakupu</th>
              <th>Nr Dokumentu</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransactions.map((row, i) => (
              <tr key={i}>
                <td title={row.contractor}>
                  {row.contractor.length > 15 ? `${row.contractor.slice(0, 15)}...` : row.contractor}
                </td>
                <td>
                  {row.value.toLocaleString("pl-PL", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </td>
                <td>{row.date}</td>
                <td>{row.document}</td>
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </TableWrapper>
    </Container>
  );
};

export default TopPurchaseTransactions;
